import { useCallback, useEffect, useMemo, useState } from "react"

import {
  getEffectiveHeavyPreviewGated,
  getHeavyPreviewGateState,
  HEAVY_PREVIEW_GATE_CHANGED_EVENT,
  HEAVY_PREVIEW_GATE_STORAGE_KEY,
  scheduleHeavyPreviewAutoGate,
  setManualHeavyPreviewGated,
} from "../utils/heavy-preview-gate-storage"

/**
 * Subscribes to device-local heavy preview gating (localStorage + same-tab events).
 * Legacy server flag migration: see HeavyPreviewLegacySeed.
 */
export function useHeavyPreviewGate(): {
  heavyPreviewsGated: boolean
  manualGated: boolean
  setManualGated: (checked: boolean) => void
  onSlowRender: () => void
} {
  const [tick, setTick] = useState(0)

  const bump = useCallback(() => {
    setTick((n) => n + 1)
  }, [])

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === HEAVY_PREVIEW_GATE_STORAGE_KEY || event.key === null) {
        bump()
      }
    }
    const onCustom = () => {
      bump()
    }
    window.addEventListener("storage", onStorage)
    window.addEventListener(HEAVY_PREVIEW_GATE_CHANGED_EVENT, onCustom)
    return () => {
      window.removeEventListener("storage", onStorage)
      window.removeEventListener(HEAVY_PREVIEW_GATE_CHANGED_EVENT, onCustom)
    }
  }, [bump])

  const state = useMemo(() => getHeavyPreviewGateState(), [tick])
  const heavyPreviewsGated = useMemo(() => getEffectiveHeavyPreviewGated(), [tick])

  const setManualGated = useCallback(
    (checked: boolean) => {
      setManualHeavyPreviewGated(checked)
      bump()
    },
    [bump]
  )

  const onSlowRender = useCallback(() => {
    scheduleHeavyPreviewAutoGate()
  }, [])

  return {
    heavyPreviewsGated,
    manualGated: state.manualGated,
    setManualGated,
    onSlowRender,
  }
}
